import { useList, useCustomMutation, useApiUrl } from "@refinedev/core";
import { useState, useMemo, useEffect } from "react";
import { FormHeader } from "@/components/forms";
import { TableToolbar } from "@/components/tables";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Globe,
  CheckCircle,
  XCircle,
  Plus,
  Loader2,
  RefreshCw,
  Languages,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { notify } from "@/lib/notify";
import { useConfirm } from "@/hooks/use-confirm";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";

interface TranslationKey {
  key: string;
  namespace: string;
  translations: Record<string, string>;
  updatedAt: string;
}

interface EditingState {
  key: string;
  translations: Record<string, string>;
}

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji: string;
  isDefault: boolean;
}

const STORAGE_KEY = "translations-visible-languages";

/**
 * TranslationsList
 *
 * Список translations с фильтрацией и управлением
 *
 * @returns {React.ReactElement} React компонент
 */
export function TranslationsList() {
  useScrollRestoration();
  const apiUrl = useApiUrl();
  const [searchQuery, setSearchQuery] = usePersistedState("translations:search", "");
  const [selectedCategory, setSelectedCategory] = usePersistedState<string>("translations:namespace", "all");
  const [editingKey, setEditingKey] = useState<EditingState | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState({ key: "", translations: {} as Record<string, string> });
  const [visibleLanguages, setVisibleLanguages] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = usePersistedState<'asc' | 'desc' | null>("translations:sort", 'asc');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const { confirm, ConfirmationDialog } = useConfirm();

  // Получаем список всех языков (как на странице /languages)
  const { result: languagesResult } = useList<Language>({
    resource: "languages",
    pagination: { pageSize: 100 },
  });

  const allLanguages = useMemo(() => {
    return (languagesResult?.data ?? []) as Language[];
  }, [languagesResult]);

  // Загружаем сохранённые языки при первом рендере
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setVisibleLanguages(parsed);
          return;
        }
      } catch {
        // ignore
      }
    }
    // По умолчанию показываем все языки
    if (allLanguages.length > 0 && visibleLanguages.length === 0) {
      setVisibleLanguages(allLanguages.map(l => l.code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLanguages]);

  // Сохраняем выбор в localStorage
  const handleLanguageToggle = (code: string) => {
    setVisibleLanguages(prev => {
      const newValue = prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newValue));
      return newValue;
    });
  };

  const handleSelectAllLanguages = () => {
    const allCodes = allLanguages.map(l => l.code);
    setVisibleLanguages(allCodes);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allCodes));
  };

  const handleDeselectAllLanguages = () => {
    setVisibleLanguages([]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
  };

  const { result, query } = useList<TranslationKey>({
    resource: "translation-keys/ui",
    pagination: { mode: 'off' },
  });

  const { mutate: updateTranslation, mutation: updateMutation } = useCustomMutation();
  const { mutate: createKey, mutation: createMutation } = useCustomMutation();
  const { mutate: deleteKeyMutate } = useCustomMutation();

  const translations = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query.isLoading;
  const refetch = query.refetch;
  const isUpdating = updateMutation?.isPending ?? false;
  const isCreating = createMutation?.isPending ?? false;

  // Языки для отображения в таблице
  const displayLanguages = useMemo(() => {
    if (visibleLanguages.length === 0) {return allLanguages;}
    return allLanguages.filter(l => visibleLanguages.includes(l.code));
  }, [allLanguages, visibleLanguages]);

  // Получение неймспейсов
  const namespaces = [...new Set(translations.map((t: TranslationKey) => t.namespace))].sort();

  // Фильтрация по поиску и неймспейсу
  const filteredTranslations = translations.filter((item: TranslationKey) => {
    const matchesSearch =
      !searchQuery || item.key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesNamespace =
      selectedCategory === "all" || item.namespace === selectedCategory;
    return matchesSearch && matchesNamespace;
  });

  // Сортировка
  const sortedTranslations = useMemo(() => {
    if (!sortOrder) { return filteredTranslations; }
    return [...filteredTranslations].sort((a, b) => {
      const cmp = a.key.localeCompare(b.key);
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [filteredTranslations, sortOrder]);

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : (prev === 'desc' ? null : 'asc'));
  };

  // Bulk selection helpers
  const allVisibleKeys = sortedTranslations.map((t: TranslationKey) => t.key);
  const allSelected = allVisibleKeys.length > 0 && allVisibleKeys.every(k => selectedKeys.has(k));
  const someSelected = allVisibleKeys.some(k => selectedKeys.has(k));

  const toggleSelectAll = () => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (allSelected) {
        for (const k of allVisibleKeys) { next.delete(k); }
      } else {
        for (const k of allVisibleKeys) { next.add(k); }
      }
      return next;
    });
  };

  const toggleSelectKey = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const keys = [...selectedKeys];
    if (keys.length === 0) { return; }
    const confirmed = await confirm({
      title: `Удалить ${keys.length} ключ${keys.length === 1 ? '' : (keys.length < 5 ? 'а' : 'ей')}?`,
      description: 'Это удалит все выбранные ключи и их переводы на всех языках. Действие необратимо.',
      variant: 'destructive',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!confirmed) { return; }

    let successCount = 0;
    let errorCount = 0;
    for (const key of keys) {
      await new Promise<void>((resolve) => {
        deleteKeyMutate(
          { url: `${apiUrl}/translations/ui/key/${encodeURIComponent(key)}`, method: 'delete', values: {} },
          {
            onSuccess: () => { successCount++; resolve(); },
            onError: () => { errorCount++; resolve(); },
          },
        );
      });
    }
    if (successCount > 0) { notify.success(`Удалено ${successCount} ключей`); }
    if (errorCount > 0) { notify.error(`Ошибка удаления ${errorCount} ключей`); }
    setSelectedKeys(new Set());
    refetch();
  };

  // Группировка по неймспейсам
  const groupedByNamespace = sortedTranslations.reduce((acc: Record<string, TranslationKey[]>, item: TranslationKey) => {
    const ns = item.namespace;
    if (!acc[ns]) {acc[ns] = [];}
    acc[ns].push(item);
    return acc;
  }, {} as Record<string, TranslationKey[]>);

  const handleEdit = (item: TranslationKey) => {
    const translationValues: Record<string, string> = {};
    // Используем все языки для редактирования
    for (const lang of allLanguages) {
      translationValues[lang.code] = item.translations?.[lang.code] ?? '';
    }
    setEditingKey({ key: item.key, translations: translationValues });
  };

  const handleSave = () => {
    if (!editingKey) {return;}

    updateTranslation(
      {
        url: `/translations/ui/key/${encodeURIComponent(editingKey.key)}`,
        method: "put",
        values: { translations: editingKey.translations },
      },
      {
        onSuccess: () => {
          notify.success("Перевод сохранён");
          setEditingKey(null);
          refetch();
        },
        onError: () => {
          notify.error("Ошибка сохранения");
        },
      }
    );
  };

  const handleCreate = () => {
    if (!newKey.key) {
      notify.error("Введите ключ");
      return;
    }

    createKey(
      {
        url: "/translations/ui/key",
        method: "post",
        values: { key: newKey.key, translations: newKey.translations },
      },
      {
        onSuccess: () => {
          notify.success("Ключ создан");
          setAddDialogOpen(false);
          setNewKey({ key: "", translations: {} });
          refetch();
        },
        onError: () => {
          notify.error("Ошибка создания");
        },
      }
    );
  };

  const handleDeleteKey = async (item: TranslationKey) => {
    const confirmed = await confirm({
      title: `Удалить ключ «${item.key}»?`,
      description: "Это удалит ключ и все его переводы на всех языках. Действие необратимо.",
      variant: "destructive",
      confirmText: "Удалить",
      cancelText: "Отмена",
    });

    if (!confirmed) { return; }

    deleteKeyMutate(
      {
        url: `${apiUrl}/translations/ui/key/${encodeURIComponent(item.key)}`,
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          notify.success(`Ключ «${item.key}» удалён`);
          refetch();
        },
        onError: () => {
          notify.error("Ошибка удаления ключа");
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title="Локализация UI"
        description={`Управление переводами интерфейса (${total} ключей)`}
        actions={
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Languages className="h-4 w-4 mr-2" />
                  Языки ({displayLanguages.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex gap-2 p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={handleSelectAllLanguages}
                  >
                    Все
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={handleDeselectAllLanguages}
                  >
                    Очистить
                  </Button>
                </div>
                <DropdownMenuSeparator />
                {allLanguages.map((lang) => (
                  <DropdownMenuCheckboxItem
                    key={lang.code}
                    checked={visibleLanguages.length === 0 || visibleLanguages.includes(lang.code)}
                    onCheckedChange={() => handleLanguageToggle(lang.code)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    <span className="mr-2">{lang.flagEmoji}</span>
                    {lang.name} ({lang.code.toUpperCase()})
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Обновить
            </Button>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить ключ
            </Button>
          </div>
        }
        sticky
      />

      {selectedKeys.size > 0 && (
        <div className="flex items-center gap-3 rounded-md bg-muted px-4 py-2 text-sm">
          <span className="text-muted-foreground">Выбрано: <strong>{selectedKeys.size}</strong></span>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => void handleDeleteSelected()}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Удалить выбранные
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedKeys(new Set())}
          >
            Снять выделение
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Поиск по ключу..."
          className="flex-1"
        />
        <Button variant="outline" size="sm" onClick={toggleSortOrder}>
          {sortOrder === 'asc' ? (
            <ArrowUp className="h-4 w-4 mr-1.5" />
          ) : sortOrder === 'desc' ? (
            <ArrowDown className="h-4 w-4 mr-1.5" />
          ) : (
            <ArrowUpDown className="h-4 w-4 mr-1.5" />
          )}
          {sortOrder === 'asc' ? 'A→Z' : (sortOrder === 'desc' ? 'Z→A' : 'Без сортировки')}
        </Button>
        <Tabs
          value={selectedCategory}
          onValueChange={setSelectedCategory}
        >
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all">Все</TabsTrigger>
            {namespaces.slice(0, 6).map((ns) => (
              <TabsTrigger key={ns} value={ns} className="text-xs">
                {ns}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Загрузка...
          </CardContent>
        </Card>
      ) : (total === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center space-y-4">
              <Globe className="h-12 w-12 mx-auto text-muted-foreground" />
              <div>
                <h3 className="font-medium">Ключи UI не найдены</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Нет ключей для интерфейса
                </p>
              </div>
              <Button onClick={() => setAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Добавить первый ключ
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedByNamespace).map(([namespace, items]) => (
          <Card key={namespace}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {namespace}
                <Badge variant="secondary">{items.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="w-8 p-2">
                        <Checkbox
                          checked={allSelected}
                          ref={(el) => { if (el) { (el as HTMLButtonElement).dataset.indeterminate = String(someSelected && !allSelected); } }}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Выбрать все"
                          className={someSelected && !allSelected ? 'data-[state=indeterminate]:opacity-70' : ''}
                        />
                      </th>
                      <th className="text-left p-2 font-medium">Ключ</th>
                      {displayLanguages.map((lang) => (
                        <th key={lang.code} className="text-left p-2 font-medium min-w-[200px]">
                          <span className="mr-1">{lang.flagEmoji}</span>
                          {lang.code.toUpperCase()}
                        </th>
                      ))}
                      <th className="w-10 p-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item: TranslationKey) => (
                      <tr
                        key={item.key}
                        className={`group border-b hover:bg-muted/50 cursor-pointer ${selectedKeys.has(item.key) ? 'bg-muted/30' : ''}`}
                        onClick={() => handleEdit(item)}
                      >
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedKeys.has(item.key)}
                            onCheckedChange={() => toggleSelectKey(item.key)}
                            aria-label={`Выбрать ${item.key}`}
                          />
                        </td>
                        <td className="p-2">
                          <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                            {item.key}
                          </code>
                        </td>
                        {displayLanguages.map((lang) => {
                          const value = item.translations?.[lang.code];
                          const isTranslated = !!value;
                          return (
                            <td key={lang.code} className="p-2">
                              <div className="flex items-start gap-2">
                                {isTranslated ? (
                                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                )}
                                <span
                                  className={`text-sm line-clamp-2 ${
                                    isTranslated ? "" : "text-muted-foreground italic"
                                  }`}
                                >
                                  {value || "Не переведено"}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => void handleDeleteKey(item)}
                            title="Удалить ключ"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      ))}

      {/* Edit Dialog */}
      <Dialog open={!!editingKey} onOpenChange={() => setEditingKey(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Редактировать перевод</DialogTitle>
          </DialogHeader>
          {editingKey && (
            <div className="space-y-4">
              <div>
                <Label>Ключ</Label>
                <Input value={editingKey.key} disabled className="bg-muted font-mono text-sm" />
              </div>
              {allLanguages.map((lang) => (
                <div key={lang.code}>
                  <Label>
                    <span className="mr-2">{lang.flagEmoji}</span>
                    {lang.name} ({lang.code.toUpperCase()})
                  </Label>
                  <Textarea
                    value={editingKey.translations[lang.code] || ""}
                    onChange={(e) =>
                      setEditingKey((prev) =>
                        prev
                          ? {
                              ...prev,
                              translations: {
                                ...prev.translations,
                                [lang.code]: e.target.value,
                              },
                            }
                          : null
                      )
                    }
                    rows={3}
                    placeholder={`Перевод на ${lang.name}...`}
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingKey(null)}>
              Отмена
            </Button>
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Добавить ключ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ключ</Label>
              <Input
                value={newKey.key}
                onChange={(e) => setNewKey((prev) => ({ ...prev, key: e.target.value }))}
                placeholder="common.button.save"
                className="font-mono text-sm"
              />
            </div>
            {allLanguages.map((lang) => (
              <div key={lang.code}>
                <Label>
                  <span className="mr-2">{lang.flagEmoji}</span>
                  {lang.name} ({lang.code.toUpperCase()})
                </Label>
                <Textarea
                  value={newKey.translations[lang.code] || ""}
                  onChange={(e) =>
                    setNewKey((prev) => ({
                      ...prev,
                      translations: {
                        ...prev.translations,
                        [lang.code]: e.target.value,
                      },
                    }))
                  }
                  rows={2}
                  placeholder={`Перевод на ${lang.name}...`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleCreate} disabled={isCreating || !newKey.key}>
              {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ConfirmationDialog}
    </div>
  );
}
